<template>
  <div class="h-screen flex flex-col justify-center">
    <div class="container max-w-screen-2xl">
      <div class="grid grid-cols-2 gap-x-16 items-center">
        <div class="">
          <img src="@/assets/img/samples/features/cancel-flows/cancel-flow-dash-hero.svg" alt="Get Started Now" class="w-full" />
        </div>
        <div class="max-w-lg">
          <p class="title"><span class="text-green-500">Lower cancellations by 42%</span> and recover over half of failed payments.</p>
          <p class="subtitle mt-3 text-brand-brown">
            Together, we'll say goodbye to debilitating churn. And you'll soon say hello to a healthier business. In the meantime, you're in control
            of this setup. How do you want to start?
          </p>
          <div class="space-y-6 mt-8" v-loading="loading">
            <div v-for="step in steps" :key="step.value" class="flex items-center space-x-4" style="max-width: 420px">
              <button
                class="flex items-center space-x-4 rounded-lg px-4 py-3 flex-grow"
                @click="handleStepClick(step)"
                :disabled="!!step.completed"
                :class="getButtonClasses(step)"
              >
                <!--
                <span class="px-2.5 py-1 border-gray-400 border-2 rounded-md bg-gray-600 text-white text-xs font-extrabold">
                  {{ step.shortcut }}
                </span>
                -->
                <span>
                  <img src="@/assets/img/spark.svg" width="32" height="32" class="-mr-2 inline-block" alt="Logo" v-if="step.icon === 'spark'" />
                  <img src="@/assets/img/rocket.svg" width="32" height="32" class="-mr-2 inline-block" alt="Logo" v-if="step.icon === 'rocket'" />
                  <img
                    src="@/assets/img/meditation.svg"
                    width="32"
                    height="32"
                    class="-mr-2 inline-block"
                    alt="Logo"
                    v-if="step.icon === 'meditation'"
                  />
                  <img src="@/assets/img/rocket.svg" width="32" height="32" class="-mr-2 inline-block" alt="Logo" v-if="step.icon === 'envelope'" />
                  <img src="@/assets/img/rocket.svg" width="32" height="32" class="-mr-2 inline-block" alt="Logo" v-if="step.icon === 'gear'" />
                  <!-- {{ step.icon }} -->
                </span>
                <span class="text-white font-bold" :class="[step.completed ? 'line-through' : '']">{{ step.label }}</span>
              </button>
              <div class="w-24">
                <div v-if="step.completed" class="text-brand-green text-sm font-semibold flex items-center space-x-1.5">
                  <font-awesome-icon :icon="['fas', 'check']" class="text-xs"></font-awesome-icon>
                  <span>Done</span>
                </div>
                <div v-else class="text-brand-black text-sm font-medium">
                  {{ step.hint }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <AIBuildModal :show="showAIBuildModal" @close="closeAIBuildModal" @submit="onAISubmit" />
  </div>
</template>

<script>
import { AI_GENERATE_BLUEPRINT, AI_REWORK_BLUEPRINT, CREATE_BLUEPRINT, SYNC_ORG } from '@/store/action-types';
import { SET_SELECTED_TEMPLATE } from '@/store/mutation-types';
import { mapGetters } from 'vuex';
import AIBuildModal from '@/components/UI/AIBuildModal.vue';
import { generateAIReferenceSteps } from '@/helpers/templates';

export default {
  name: 'GoToBuilderCard',
  props: {
    hasFlows: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    AIBuildModal,
  },
  data() {
    return {
      loading: false,
      showAIBuildModal: false,
    };
  },
  computed: {
    ...mapGetters(['dunningOnboardState', 'primaryBlueprintId', 'orgCanUseAI']),
    onboardSteps() {
      const steps = [];
      const regularSteup = { value: 'flow', label: 'Set up first cancel flow', icon: 'gear', hint: '5 mins' };
      const aiSetup = { value: 'ai', label: 'Build a flow with AI', icon: 'spark', hint: '1-2 mins', style: 'ai' };
      const install = { value: 'install', label: 'Drop in the embed code', icon: 'meditation', hint: '5-10 mins' };
      const push = { value: 'push', label: 'Push your first flow live', icon: 'rocket', hint: 'One click' };
      const dunning = { value: 'recover', label: 'Recover failed payments', icon: 'envelope', hint: '5 mins' };

      // Right now this is always true. We may want to feature gate it later, or roll out to a subset of users.
      if (this.orgCanUseAI) {
        steps.push(aiSetup);
      } else {
        steps.push(regularSteup);
      }
      steps.push(install);
      // steps.push(push);
      steps.push(dunning);
      return steps;
    },
    steps() {
      const hasFlows = !!this.hasFlows; // prop that forces 'flow' step to be completed
      return this.onboardSteps.map((step) => ({
        ...step,
        // flow and install are always false if this component is visible
        completed: this.dunningOnboardState[step.value] || (hasFlows && ['flow', 'ai'].includes(step.value)),
      }));
    },
  },
  methods: {
    async handleStepClick(step) {
      if (step.value === 'flow') {
        this.createBlueprint();
      }
      if (step.value === 'ai') {
        this.showAIBuildModal = true;
      }
      if (step.value === 'install') {
        this.$router.push('/settings/embed');
      }
      if (step.value === 'recover') {
        this.$router.push('/dunning/metrics');
      }
    },
    closeAIBuildModal() {
      this.showAIBuildModal = false;
    },
    async onAISubmit(prompt) {
      // console.log('%cAI onboarding submit >>>', 'color: blue; font-weight: bold;');
      this.loading = true;
      this.showAIBuildModal = false;
      const refSteps = generateAIReferenceSteps();

      if (!this.$store.getters.primaryBlueprintId) {
        await this.$store.dispatch(CREATE_BLUEPRINT, { aiGenerated: true, aiPrompt: prompt });
        await this.$store.dispatch(SYNC_ORG);
        const primaryGuid = this.$store.getters.primaryBlueprint.guid;
        await this.$store.dispatch(AI_GENERATE_BLUEPRINT, { blueprintGuid: primaryGuid, instructions: prompt, steps: refSteps });
      } else {
        const primaryGuid = this.$store.getters.primaryBlueprint.guid;
        await this.$store.commit(SET_SELECTED_TEMPLATE, '');
        await this.$store.dispatch(AI_REWORK_BLUEPRINT, { blueprintGuid: primaryGuid, instructions: prompt, steps: refSteps });
      }

      // console.log('%c ### >>> Done with the actions! Redirecting...', 'background-color: #0f0; font-weight: bold;');
      this.$router.push({
        name: 'builder',
        params: { id: this.primaryBlueprintId },
        // query: { ai: true },
      });
    },
    async createBlueprint() {
      this.loading = true;
      this.creatingBlueprint = true;
      // create a blueprint if there isn't one
      if (!this.primaryBlueprintId) {
        await this.$store.dispatch(CREATE_BLUEPRINT);
        await this.$store.dispatch(SYNC_ORG);
      }
      this.$router.push({ name: 'builder', params: { id: this.primaryBlueprintId } });
    },
    getButtonClasses(step) {
      if (step.style === 'ai') {
        if (step.completed) return ['cursor-default opacity-80 bg-gradient-to-l from-brand-ai-pink to-brand-ai-purple'];
        return ['bg-gradient-to-l from-brand-ai-pink to-brand-ai-purple cursor-pointer hover:opacity-90'];
      }
      if (step.completed) return ['cursor-default bg-gray-600'];
      return ['bg-gray-800 cursor-pointer hover:bg-gray-700 transition-colors'];
    },
  },
};
</script>
